import { mapDTO } from '@twistezo/ts-dto-mapper'
import { isNil } from 'lodash'

import { SortInput } from 'components/Table'
import { alertsFromDTO } from 'features/alerts/api/mappers/alertFromDTO'
import { AdUnitBidderAlert } from 'features/globalSetup/api/types/adUnitBidderAlert'
import { AdUnitParametersDTO } from 'features/globalSetup/api/types/adUnitBidderDTO'
import { AdUnitParameter, AdUnitParameters } from 'features/globalSetup/types/adUnitBidder'
import { DomainBidder, DomainBidderParameter } from 'features/globalSetup/types/domainBidder'
import { withoutOuterQuotes } from 'utils/string'
import { AdUnitSortBy } from '../../../components/AdUnitsTable/types'
import { AdUnitBidderSlice } from '../types'
import { AdUnitBidderSliceDTO, AdUnitSortByDTO } from './types'

type From = AdUnitBidderSliceDTO
type To = AdUnitBidderSlice

export const fromDTO = (
  from: From,
  csParamsSchema: DomainBidder['csParamsSchema'],
  s2sParamsSchema: DomainBidder['s2sParamsSchema'],
): To =>
  mapDTO<From, To>({ from }).transform((adUnitBidder: From): To => {
    const {
      adUnit: { divId, id: adUnitId, path, ybAdUnitEnabled },
      csEnabled,
      csParams,
      fieldAlerts,
      id,
      s2sEnabled,
      s2sParams,
      updatedAt,
    } = adUnitBidder

    return {
      adUnit: {
        divId: divId || undefined,
        id: adUnitId,
        path,
        ybAdUnitEnabled: ybAdUnitEnabled ?? undefined,
      },
      alerts: alertsFromDTO<AdUnitBidderAlert>(fieldAlerts),
      csEnabled,
      csParams: paramsFromDTO(csParams, csParamsSchema),
      id,
      s2sEnabled,
      s2sParams: paramsFromDTO(s2sParams, s2sParamsSchema),
      updatedAt: new Date(updatedAt),
    }
  })

export const paramsFromDTO = (
  adUnitParams: AdUnitParametersDTO,
  domainParamsSchema: DomainBidder['csParamsSchema'] | DomainBidder['s2sParamsSchema'],
): AdUnitParameters =>
  Object.fromEntries(
    domainParamsSchema.map(
      (schema: DomainBidderParameter): [keyof AdUnitParameters, AdUnitParameter] => {
        const { name, type } = schema

        const value: unknown = adUnitParams[name]
        let parsedValue: string | undefined = undefined

        if (isNil(value)) {
          parsedValue = undefined
        } else if (type === 'string') {
          parsedValue = withoutOuterQuotes(JSON.stringify(value))
        } else if (type === 'integer' || type === 'float') {
          parsedValue = Number(value).toLocaleString('pl-PL', { useGrouping: false })
        }

        return [name, { ...schema, value: parsedValue }]
      },
    ),
  )

export const sortToDTO = (sort: SortInput<AdUnitSortBy>): SortInput<AdUnitSortByDTO> => {
  const { by } = sort

  if (by === 'adUnit.path') {
    return { ...sort, by: 'adUnitPath' }
  } else if (by === 'adUnit.divId') {
    return { ...sort, by: 'adUnitDivId' }
  } else {
    return { ...sort, by }
  }
}
